import { Stack, TableCell, TableRow } from '@mui/material'
import React from 'react'
import { Link, NavLink } from 'react-router'
import { formatBigMoney, formatDateYYYYMMDD, formatNumber } from '../../../../packages/utils/numberFormatters'
import { WarrantEntity } from '../../../../types/WarrantEntity'
import { HasWarrantLabel } from '../../../../components/entities/IssueTypeLabels'
import { normalizedWarrantName } from '@finance-lab/utils/shared'

export const WarrantHistoryTableRow = ({
    date,
    warrant,
    locale,
    multipleInstrumentsExists = false,
    splitAdjust = false,
    isSmallScreen,
}: {
    date: Date
    warrant: WarrantEntity
    locale: string
    multipleInstrumentsExists: boolean
    splitAdjust: boolean
    isSmallScreen: boolean
}) => {
    // sumSharesSubscribed is the sum of all shares subscribed in all subscription periods, or null if all values are null
    const sumSharesSubscribed = warrant.subscriptionPeriods.reduce((acc, sp) => {
        if (sp.sharesSubscribed === null) {
            return null
        } else if (acc === null) {
            return sp.sharesSubscribed
        } else {
            return acc + sp.sharesSubscribed
        }
    }, null)
    const { warrantSubPrice } = warrant.subscriptionPeriods[0]
    const hasTotalGuarantorAllocation = warrant.totalGuarantorAllocation !== null
    const hasTotalGuarantorRemunerationShares = warrant.totalGuarantorRemunerationShares !== null

    return (
        <>
            <TableRow
                sx={{
                    fontStyle: warrant.cancelled ? 'italic' : 'normal',
                    '& .MuiTableCell-root': {
                        ...(hasTotalGuarantorAllocation || hasTotalGuarantorRemunerationShares
                            ? {
                                  borderBottom: 0,
                              }
                            : {}),
                    },
                }}
            >
                <TableCell>{formatDateYYYYMMDD(date)}</TableCell>
                {multipleInstrumentsExists && !isSmallScreen ? <TableCell>{warrant.instrumentSymbol}</TableCell> : null}
                <TableCell>
                    <Stack direction='row' spacing={2}>
                        <HasWarrantLabel />
                        <Link component={NavLink} to={`/warrants/${warrant.id}`}>
                            {normalizedWarrantName(warrant)}
                        </Link>
                    </Stack>
                </TableCell>
                {!isSmallScreen ? (
                    <>
                        <TableCell>
                            {warrantSubPrice
                                ? formatNumber(warrantSubPrice * (splitAdjust ? warrant.splitAdjustment : 1), {
                                      locale,
                                      minDecimals: 2,
                                      maxDecimals: 5,
                                  })
                                : '-'}
                        </TableCell>
                        <TableCell>
                            {warrant.cancelled
                                ? 'Avbruten'
                                : sumSharesSubscribed === null
                                  ? 'Inte kommunicerat ännu'
                                  : formatNumber(sumSharesSubscribed / (splitAdjust ? warrant.splitAdjustment : 1), {
                                        locale,
                                        decimals: 0,
                                    })}
                        </TableCell>
                    </>
                ) : null}
                <TableCell>
                    {sumSharesSubscribed !== null && warrantSubPrice
                        ? formatBigMoney(sumSharesSubscribed * warrantSubPrice, { locale, decimals: 0, tooltip: true })
                        : '-'}
                </TableCell>
            </TableRow>

            {hasTotalGuarantorAllocation && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(hasTotalGuarantorRemunerationShares
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>Riktad emission till garanter</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(warrant.totalGuarantorAllocation, { locale, decimals: 0 })}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {formatBigMoney(warrant.totalGuarantorAllocationSize, { locale, decimals: 0, tooltip: true })}
                    </TableCell>
                </TableRow>
            )}

            {hasTotalGuarantorRemunerationShares && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>Garantiersättning</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(warrant.totalGuarantorRemunerationShares, { locale, decimals: 0 })}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {formatBigMoney(warrant.totalGuarantorRemunerationSharesSize, {
                            locale,
                            decimals: 0,
                            tooltip: true,
                        })}
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}
